import { FileRepresentation } from '@/components/form-builder/form-builder.types';
import { FileService, NAMESPACE_ENUM } from '@/components/shared/file-list/file-list.service';
import { MeetingRepository } from '@/store/meeting-state/meeting-state.repository';
import { ResponseDoc, WithMetadata } from '@/types';
import {
  DacMeeting,
  DemandeDto,
  Meeting,
  MeetingCncmpValidationDto,
  MeetingCpmpValidationDto,
  MeetingDto,
  PlannedDossier,
  PpmMeeting, ReportMeeting,
} from '@/types/meeting.types';
import { inject, Injectable } from '@angular/core';

export enum COMMITTEE_ENUM {
  CPMP = 'CPMP',
  CNCMP = 'CNCMP',
  CNCMP_S = 'CNCMP-S',
}

@Injectable({
  providedIn: 'root',
})
export class MeetingTransformationService {
  meetingRepository = inject(MeetingRepository);
  fileService = inject(FileService);

  mergePlannedDossiers(): PlannedDossier[] {
    const reunionPpms = this.meetingRepository.getNested<any[]>('dto', 'reunionPpms');
    const reunionDacs = this.meetingRepository.getNested<any[]>('dto', 'reunionDacs');
    const reunionDemandes = this.meetingRepository.getNested<any[]>('dto', 'reunionDemandes');
    const reunionReports = this.meetingRepository.getNested<any[]>('dto', 'reunionReports');

    this.meetingRepository.set('plannedDossiers', [
      ...reunionPpms.map(ppm => ({
        id: ppm.id,
        version: ppm.version,
        processInstanceId: ppm.processInstanceId,
        taskId: ppm.taskId,
        nom: ppm.nom,
        ref: ppm.ref,
        type: 'PPM',
        ac: ppm.ac,
        observation: ppm.observation,
        decision: ppm.decision,
        docs: ppm.docs ?? [],
      })),
      ...reunionDacs.map(dac => ({
        id: dac.dacId,
        version: dac.dacVer,
        processInstanceId: dac.processInstanceId,
        taskId: dac.taskId,
        nom: dac.nom,
        ref: dac.ref,
        type: 'DAC',
        ac: dac.ac,
        observation: dac.observation,
        decision: dac.decision,
        docs: dac.docs ?? [],
      })),
      ...reunionDemandes.map(demande => ({
        id: demande.id,
        version: demande.version,
        processInstanceId: demande.processInstanceId,
        taskId: demande.taskId,
        ref: demande.ref,
        nom: demande.nom,
        type: 'ADDITIF',
        ac: demande.ac,
        observation: demande.observation,
        decision: demande.decision,
        docs: demande.docs ?? [],
      })),
      ...reunionReports.map(report => ({
        id: report.id,
        version: report.version,
        processInstanceId: report.processInstanceId,
        taskId: report.taskId,
        nom: report.nom,
        ref: report.ref,
        type: 'REPORT',
        ac: report.ac,
        observation: report.observation,
        decision: report.decision,
        docs: report.docs ?? [],
      })),
    ]);
    return this.meetingRepository.get('plannedDossiers');
  }

  buildValidationGroupArrayDto(meetingDto: MeetingDto, type: COMMITTEE_ENUM, dossierType: 'PPM' | 'DAC' | 'ADDITIF'|'REPORT'): FormData {
    let data: any[] = [];
    if (dossierType === 'PPM') {
      data = this.buildValidateGroupObject(type, meetingDto.reunionPpms, 'PPM');
    } else if (dossierType === 'DAC') {
      data = this.buildValidateGroupObject(type, meetingDto.reunionDacs, 'DAC');
    } else if (dossierType === 'ADDITIF') {
      data = this.buildValidateGroupObject(type, meetingDto.reunionDemandes, 'ADDITIF');
    }
    else if (dossierType === 'REPORT') {
      data = this.buildValidateGroupObject(type, meetingDto.reunionReports, 'REPORT');
    }
    const formData = new FormData();
    if (data.length > 0) formData.append('requestsJson', JSON.stringify(data));
    return formData;
  }

  buildMeetingDtoToFormData(meetingDto: MeetingDto) {
    const formData = new FormData();
    formData.append('comId', meetingDto.comId.toString());
    formData.append('object', meetingDto.object);
    formData.append('emplacement', meetingDto.emplacement);
    if (meetingDto.note) formData.append('note', meetingDto.note);

    meetingDto.reunionPpms.forEach((ppm, idx) => {
      formData.append(`reunionPpms[${idx}].id`, ppm.id.toString());
      formData.append(`reunionPpms[${idx}].version`, ppm.version.toString());
      formData.append(`reunionPpms[${idx}].processInstanceId`, ppm.processInstanceId);
      formData.append(`reunionPpms[${idx}].taskId`, ppm.taskId);
      if (ppm.decision?.includes('=')) {
        formData.append(`reunionPpms[${idx}].decision`, ppm.decision?.split('=')[1].trim());
      } else {
        formData.append(
          `reunionPpms[${idx}].decision`,
          ppm.decision == null
            ? ''
            : ({
                Approuver: 'APPROVE',
                APPROVE: 'APPROVE',
                Rejeter: 'REJECT',
                REJECT: 'REJECT',
                'Révision SCS': 'CI',
                CI: 'CI',
                Rejet: 'REJECT_END',
                REJECT_END: 'REJECT_END',
                'Renvoi pour réexamen': 'REPORT',
                REPORT: 'REPORT',
                'Approuver sous réserve': 'COND_APPROVE',
                COND_APPROVE: 'COND_APPROVE',
              }[ppm.decision] ?? ppm.decision)
        );
      }
      formData.append(`reunionPpms[${idx}].observation`, ppm.observation ?? '');
      ppm.docs?.forEach((doc, index) => {
        formData.append(`reunionPpms[${idx}].docsPpm[${index}].id`, doc.id.toString());
      });

      ppm.files?.forEach(file => {
        formData.append(`reunionPpms[${idx}].files`, file);
      });
    });

    meetingDto.reunionDacs.forEach((dac, idx) => {
      formData.append(`reunionDacs[${idx}].dacId`, dac.dacId.toString());
      formData.append(`reunionDacs[${idx}].dacVer`, dac.dacVer.toString());
      formData.append(`reunionDacs[${idx}].processInstanceId`, dac.processInstanceId);
      formData.append(`reunionDacs[${idx}].taskId`, dac.taskId);
      formData.append(`reunionDacs[${idx}].decision`, dac.decision ?? '');
      formData.append(`reunionDacs[${idx}].observation`, dac.observation ?? '');
      dac.files?.forEach(file => {
        formData.append(`reunionDacs[${idx}].files`, file);
      });
      dac.docs?.forEach((doc, index) => {
        formData.append(`reunionDacs[${idx}].docs[${index}].id`, doc.id.toString());
      });
    });

    meetingDto.participants.forEach((participant, idx) => {
      formData.append(`participants[${idx}].id`, participant.id?.toString() ?? '');
      formData.append(`participants[${idx}].nom`, participant.nom);
      formData.append(`participants[${idx}].prenom`, participant.prenom);
      formData.append(`participants[${idx}].mail`, participant.mail);
      formData.append(`participants[${idx}].fonction`, participant.fonction ?? '');
      formData.append(`participants[${idx}].organisme`, participant.organisme ?? '');
      formData.append(`participants[${idx}].present`, participant.present.toString());
      formData.append(`participants[${idx}].userId`, participant.userId ?? '');
      formData.append(`participants[${idx}].fonctionReunion`, participant.fonctionReunion ?? '');
    });

    meetingDto.reunionDemandes.forEach((question, idx) => {
      formData.append(`reunionDemandes[${idx}].id`, question.id.toString());
      formData.append(`reunionDemandes[${idx}].version`, question.version.toString());
      formData.append(`reunionDemandes[${idx}].processInstanceId`, question.processInstanceId);
      formData.append(`reunionDemandes[${idx}].taskId`, question.taskId);
      formData.append(`reunionDemandes[${idx}].decision`, question.decision ?? '');
      formData.append(`reunionDemandes[${idx}].observation`, question.observation ?? '');
      question.files?.forEach(file => {
        formData.append(`reunionDemandes[${idx}].files`, file);
      });
      question.docs?.forEach((doc, index) => {
        formData.append(`reunionDemandes[${idx}].docsDemande[${index}].id`, doc.id.toString());
      });
    });
    meetingDto.reunionReports.forEach((report, idx) => {
      formData.append(`reunionReports[${idx}].id`, report.id.toString());
      formData.append(`reunionReports[${idx}].version`, report.version.toString());
      formData.append(`reunionReports[${idx}].processInstanceId`, report.processInstanceId);
      formData.append(`reunionReports[${idx}].taskId`, report.taskId);
      formData.append(`reunionReports[${idx}].decision`, report.decision ?? '');
      formData.append(`reunionReports[${idx}].observation`, report.observation ?? '');
      report.docs?.forEach((doc, index) => {
        formData.append(`reunionReports[${idx}].docsReport[${index}].id`, doc.id.toString());
      });
      report.files?.forEach(file => {
        formData.append(`reunionReports[${idx}].files`, file);
      });
    });

    return formData;
  }

  origanizeDossiers(dossiers: PlannedDossier[]) {
    const ppmDossiers = dossiers.filter(dossier => dossier.type === 'PPM');
    const dacDossiers = dossiers.filter(dossier => dossier.type === 'DAC');
    const demandeDossiers = dossiers.filter(dossier => dossier.type === 'ADDITIF');
    const reportDossier = dossiers.filter(dossier => dossier.type === 'REPORT');

    this.meetingRepository.setNested(
      'dto',
      'reunionPpms',
      ppmDossiers.map(ppm => {
        return {
          id: ppm.id,
          version: ppm.version,
          processInstanceId: ppm.processInstanceId,
          taskId: ppm.taskId,
          decision: ppm.decision,
          observation: ppm.observation,
          files: ppm.files ?? [],
          docs: ppm.docs ?? [],
        };
      })
    );
    this.meetingRepository.setNested(
      'dto',
      'reunionDacs',
      dacDossiers.map(dac => {
        return {
          dacId: dac.id,
          dacVer: dac.version,
          processInstanceId: dac.processInstanceId,
          taskId: dac.taskId,
          decision: dac.decision,
          observation: dac.observation,
          files: dac.files ?? [],
          docs: dac.docs ?? [],
        };
      })
    );
    this.meetingRepository.setNested(
      'dto',
      'reunionDemandes',
      demandeDossiers.map(demande => ({
        id: demande.id,
        version: demande.version,
        processInstanceId: demande.processInstanceId,
        taskId: demande.taskId,
        decision: demande.decision,
        observation: demande.observation,
        files: demande.files ?? [],
        docs: demande.docs ?? [],
      }))
    );

    this.meetingRepository.setNested(
      'dto',
      'reunionReports',
      reportDossier.map(report => ({
        id: report.id,
        version: report.version,
        processInstanceId: report.processInstanceId,
        taskId: report.taskId,
        decision: report.decision,
        observation: report.observation,
        files: report.files ?? [],
        docs: report.docs ?? [],
      }))
    );
  }

  prepareDtoForSaveOrValidate(meetingDto: MeetingDto) {
    this.origanizeDossiers(this.meetingRepository.get('plannedDossiers'));
    return this.buildMeetingDtoToFormData(meetingDto);
  }

  inspectPv() {
    const file: ResponseDoc = this.meetingRepository.getNested<ResponseDoc>('response', 'processVerbal', 'document', 'doc');
    this.fileService.download(this.responseDocToFileRepresentation(file), NAMESPACE_ENUM.PV).subscribe();
  }

  inspectSnippets(doc: ResponseDoc) {
    this.fileService.download(this.responseDocToFileRepresentation(doc), NAMESPACE_ENUM.SNIPPET).subscribe();
  }

  private responseDocToFileRepresentation(doc: ResponseDoc): FileRepresentation {
    return {
      alfresco_id: doc.alfresco_id,
      filename: doc.name,
      extension: 'pdf',
      size: doc.fileSize,
    };
  }

  buildValidateGroupObject(type: COMMITTEE_ENUM, requests: DacMeeting[] | PpmMeeting[] | DemandeDto[] | ReportMeeting[], dossierType: 'PPM' | 'DAC' | 'ADDITIF' | 'REPORT') {
    return requests
      .map(r => {
        if (!r.decision) return;
        if (type === COMMITTEE_ENUM.CPMP && dossierType === 'DAC') {
          return {
            taskId: r.taskId,
            comment: r.observation,
            cpmpDecision: r.decision,
          } as MeetingCpmpValidationDto;
        } else if (type === COMMITTEE_ENUM.CPMP) {
          return {
            taskId: r.taskId,
            comment: r.observation,
            validate: r.decision === 'Validate' || r.decision === 'Valider' || r.decision === 'true' ? true : false,
          };
        } else if (type === COMMITTEE_ENUM.CNCMP_S) {
          return {
            taskId: r.taskId,
            comment: r.observation,
            validate: true,
          };
        } else {
          return {
            taskId: r.taskId,
            comment: r.observation,
            cncmpDecision:
              dossierType !== 'PPM'
                ? r.decision === 'Approuver' || r.decision === 'APPROVE'
                  ? 'APPROVE'
                  : r.decision === 'Rejeter' || r.decision === 'REJECT'
                    ? 'REJECT'
                    : r.decision === 'Révision SCS' || r.decision === 'CI'
                      ? 'CI'
                      : r.decision === 'Rejet' || r.decision === 'REJECT_END'
                        ? 'REJECT_END'
                        : r.decision === 'Renvoi pour réexamen' || r.decision === 'REPORT'
                          ? 'REPORT'
                          : r.decision === 'COND_APPROVE' || r.decision === 'Approuver sous réserve'
                            ? 'COND_APPROVE'
                            : undefined
                : r.decision,
          } as MeetingCncmpValidationDto;
        }
      })
      .filter(Boolean);
  }

  setResponseToDtoOnLoadData(response: WithMetadata<Meeting>): void {
    this.meetingRepository.set('response', response);
    this.meetingRepository.set('dto', {
      comId: response.com.id,
      object: response.object,
      emplacement: response.emplacement ?? '',
      reunionDate: response.reunionDate,
      note: response.note,
      reunionPpms: response.reunionPpms.map(ppm => ({
        id: ppm.ppm.id,
        version: ppm.ppm.version,
        processInstanceId: ppm.processInstanceId,
        taskId: ppm.taskId,
        ref: ppm.ppm.ref,
        nom: ppm.ppm.nom,
        ac: ppm.ppm.ac,
        decision: ppm.decision,
        observation: ppm.observation,
        docs: ppm.docs ?? [],
      })),
      reunionDacs: response.reunionDacs.map(dac => ({
        dacId: dac.dac.dacId,
        dacVer: dac.dac.dacVer,
        processInstanceId: dac.processInstanceId,
        taskId: dac.taskId,
        ref: dac.dac.reference,
        nom: dac.dac.nom,
        ac: dac.dac.ac,
        decision: dac.decision,
        observation: dac.observation,
        docs: dac.docs ?? [],
      })),
      reunionDemandes: response.reunionDemandes.map(demande => ({
        id: demande.demande.id,
        version: demande.demande.version,
        processInstanceId: demande.processInstanceId,
        taskId: demande.taskId,
        ref: demande.demande.reference,
        ac: demande.ac,
        decision: demande.decision,
        observation: demande.observation,
        docs: demande.docs ?? [],
      })),
      reunionReports: response.reunionReports.map(report => ({
        id: report.report.id,
        version: report.report.version,
        processInstanceId: report.processInstanceId,
        taskId: report.taskId,
        ref: report.report.reference,
        ac: report.ac,
        decision: report.decision,
        observation: report.observation,
        docs: report.docs ?? [],
      })),
      participants: response.participants,
      haveExtraits: response.haveExtraits,
    });


  }
}
